import React from "react";

function safeParse(value, fallback) {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
}

export default function usePersistentForm(storageKey, initialState) {
  const initialSavedValue = React.useMemo(() => {
    if (!storageKey) return null;
    return safeParse(localStorage.getItem(storageKey), null);
  }, [storageKey]);

  const hasStoredDraft = !!(
    initialSavedValue && typeof initialSavedValue === "object"
  );

  const [form, setForm] = React.useState(() => {
    if (!hasStoredDraft) return initialState;

    return {
      ...initialState,
      ...initialSavedValue,
    };
  });

  React.useEffect(() => {
    if (!storageKey) return;

    try {
      localStorage.setItem(storageKey, JSON.stringify(form));
    } catch (error) {
      console.error("Failed to persist form state", error);
    }
  }, [storageKey, form]);

  const resetForm = React.useCallback(
    (nextState = initialState) => {
      setForm(nextState);
      try {
        localStorage.setItem(storageKey, JSON.stringify(nextState));
      } catch (error) {
        console.error("Failed to reset persisted form state", error);
      }
    },
    [initialState, storageKey],
  );

  const clearStoredForm = React.useCallback(() => {
    try {
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.error("Failed to clear persisted form state", error);
    }
  }, [storageKey]);

  const hydrateForm = React.useCallback(
    (nextState, { overwriteStorage = true } = {}) => {
      setForm(nextState);

      if (overwriteStorage) {
        try {
          localStorage.setItem(storageKey, JSON.stringify(nextState));
        } catch (error) {
          console.error("Failed to hydrate persisted form state", error);
        }
      }
    },
    [storageKey],
  );

  return {
    form,
    setForm,
    resetForm,
    hydrateForm,
    clearStoredForm,
    hasStoredDraft,
  };
}
